import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import useOnScreen from "../../hooks/useOnScreen";

const curriculumPages = [
  [
    {
      meeting: "1",
      topic: "Writing Task 1",
      details: [
        "Writing 1 Concept",
        "In-Depth Understanding: Line Graph, Bar Chart Skill, Pie Chart",
        "Table Practice",
      ],
    },
    {
      meeting: "2",
      topic: "Speaking",
      details: [
        "Speaking Concept",
        "Fluency",
        "Coherence",
        "Lexical Resources",
        "Pronunciation",
      ],
    },
    {
      meeting: "3",
      topic: "Reading",
      details: ["Reading Concept", "Practice and Review"],
    },
    {
      meeting: "4",
      topic: "Listening",
      details: ["Listening Concept", "Listening Part 1, 2, 3, and 4"],
    },
    {
      meeting: "5",
      topic: "Writing Task 1 & 2",
      details: [
        "In-Depth Understanding: Map Skill, Process, Mixed Figures",
        "Practice",
        "Writing 2 Concept",
        "Deeply Explanation of TS-SS-SD",
      ],
    },
  ],
  [
    {
      meeting: "6",
      topic: "Speaking",
      details: ["Grammatical Range and Accuracy", "Practice Part 1"],
    },
    {
      meeting: "7",
      topic: "Writing Task 2",
      details: [
        "In-depth understanding:",
        "- Opinion-led",
        "- Argument-led",
        "- Advantage & Disadvantage",
        "- Problem issue type",
        "- Mixed type",
        "Practice brainstorming",
      ],
    },
    {
      meeting: "8",
      topic: "Speaking",
      details: ["Speaking Part 2 and 3", "Practice all parts"],
    },
    {
      meeting: "9",
      topic: "Customizable Class",
      details: ["Customized based on student requests"],
    },
    {
      meeting: "10",
      topic: "Scholarship Preparation",
      details: ["How to Ace Scholarship Applications"],
    },
  ],
];

const Curriculum = () => {
  const [currentPage, setCurrentPage] = useState(0);
  const [ref, isVisible] = useOnScreen({ threshold: 0.1 });

  const [openMeeting, setOpenMeeting] = useState(curriculumPages[0][0].meeting);

  const nextPage = () => setCurrentPage((prev) => (prev + 1) % curriculumPages.length);
  const prevPage = () => setCurrentPage((prev) => (prev - 1 + curriculumPages.length) % curriculumPages.length);

  useEffect(() => {
    setOpenMeeting(curriculumPages[currentPage][0].meeting);
  }, [currentPage]);

  return (
    <section id="curriculum" className="st-surface-cream overflow-x-clip px-4 py-14 sm:py-16" ref={ref}>
      <div className="mx-auto w-full max-w-6xl">
        <div className={`text-center ${isVisible ? "animate__animated animate__fadeInUp" : ""}`}>
          <h2 className="st-font-display text-[42px] leading-[1.08] text-[#262626] sm:text-[52px] lg:text-[66px]">
            <span className="italic">Progres</span> <span className="font-bold">Belajar</span> <span className="italic">yang</span>
            <br />
            <span className="font-bold">Terukur</span> <span className="italic">di Setiap Sesi</span>
          </h2>

          <p className="st-font-body st-text-body-muted mx-auto mt-5 max-w-3xl text-[20px] leading-[1.35] sm:text-[24px]">
            Dari basic hingga <span className="font-bold">strategi</span> lanjutan, semua disusun untuk
            <br className="hidden sm:block" />
            bantu kamu <span className="font-bold">mencapai target</span> skor
          </p>
        </div>

        <div className={`relative mt-10 ${isVisible ? "animate__animated animate__fadeInUp" : ""}`}>
          <div className="mb-4 flex items-center justify-between md:hidden">
            <button
              type="button"
              onClick={prevPage}
              className="st-curriculum-nav st-focus-ring"
              aria-label="Previous curriculum page"
            >
              <ChevronLeft size={20} />
            </button>
            <p className="st-font-body st-text-body text-sm font-semibold">Page {currentPage + 1} / {curriculumPages.length}</p>
            <button
              type="button"
              onClick={nextPage}
              className="st-curriculum-nav st-focus-ring"
              aria-label="Next curriculum page"
            >
              <ChevronRight size={20} />
            </button>
          </div>

          <div className="hidden overflow-x-auto rounded-[10px] md:block">
            <table className="st-font-body w-full min-w-[760px] border-separate border-spacing-y-3 text-left">
              <thead>
                <tr className="st-curriculum-head">
                  <th className="rounded-l-[6px] px-5 py-3 text-center text-[20px] font-bold">Meeting</th>
                  <th className="px-5 py-3 text-center text-[20px] font-bold">Topic</th>
                  <th className="rounded-r-[6px] px-5 py-3 text-[20px] font-bold">Details</th>
                </tr>
              </thead>
              <tbody>
                {curriculumPages[currentPage].map((row, index) => (
                  <tr key={`${row.meeting}-${row.topic}`} className={index % 2 === 1 ? "st-curriculum-row-alt" : ""}>
                    <td className="rounded-l-[6px] px-5 py-4 text-center align-middle text-[20px] text-[#2D2D2D]">{row.meeting}</td>
                    <td className="px-5 py-4 text-center align-middle text-[20px] leading-[1.2] text-[#2D2D2D]">{row.topic}</td>
                    <td className="rounded-r-[6px] px-5 py-4 align-top text-[20px] leading-[1.2] text-[#2D2D2D]">
                      <ul className="space-y-1">
                        {row.details.map((detail, detailIndex) => {
                          const isSubPoint = detail.trim().startsWith("-");

                          if (isSubPoint) {
                            return (
                              <li key={`${row.meeting}-${detailIndex}`} className="ml-5">
                                {detail}
                              </li>
                            );
                          }

                          return (
                            <li key={`${row.meeting}-${detailIndex}`} className="flex gap-2">
                              <span>•</span>
                              <span>{detail}</span>
                            </li>
                          );
                        })}
                      </ul>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="space-y-3 md:hidden">
            {curriculumPages[currentPage].map((row, index) => (
              <article
                key={`mobile-${row.meeting}-${row.topic}`}
                className={`rounded-[10px] border border-[#D0CECB] bg-white p-4 ${index % 2 === 1 ? "st-curriculum-row-alt" : ""}`}
              >
                <button
                  type="button"
                  onClick={() => setOpenMeeting((prev) => (prev === row.meeting ? "" : row.meeting))}
                  className="st-focus-ring flex w-full items-center justify-between text-left"
                >
                  <div>
                    <p className="st-font-body text-xs font-semibold uppercase tracking-[0.08em] text-[#6C6765]">Meeting {row.meeting}</p>
                    <p className="st-font-body mt-1 text-[20px] font-semibold leading-[1.2] text-[#2D2D2D]">{row.topic}</p>
                  </div>
                  <span className="st-font-body text-[28px] leading-none text-[#202A60]">{openMeeting === row.meeting ? "-" : "+"}</span>
                </button>

                {openMeeting === row.meeting ? (
                  <ul className="st-font-body mt-3 space-y-1 border-t border-[#C8C5C2] pt-3 text-[16px] leading-[1.25] text-[#2D2D2D]">
                    {row.details.map((detail, detailIndex) => {
                      const isSubPoint = detail.trim().startsWith("-");

                      if (isSubPoint) {
                        return (
                          <li key={`mobile-${row.meeting}-${detailIndex}`} className="ml-4">
                            {detail}
                          </li>
                        );
                      }

                      return (
                        <li key={`mobile-${row.meeting}-${detailIndex}`} className="flex gap-2">
                          <span>•</span>
                          <span>{detail}</span>
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
              </article>
            ))}
          </div>

          <button
            type="button"
            onClick={prevPage}
            className="st-curriculum-nav st-focus-ring absolute -left-3 top-1/2 z-10 hidden -translate-y-1/2 sm:-left-5 md:inline-flex"
            aria-label="Previous curriculum page"
          >
            <ChevronLeft size={20} />
          </button>

          <button
            type="button"
            onClick={nextPage}
            className="st-curriculum-nav st-focus-ring absolute -right-3 top-1/2 z-10 hidden -translate-y-1/2 sm:-right-5 md:inline-flex"
            aria-label="Next curriculum page"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default Curriculum;
